import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentProps } from 'react'

import { useResourceRecordsContext } from '@/shared/hooks'
import { useTranslation } from '@/shared/hooks/useTranslation'

import { applyAdvancedFilters } from '../model/advancedFiltering'
import {
  filterByMoment,
  partitionByTemporalStatus,
  searchSessions
} from '../model/filtering'
import { deriveFilterOptions } from '../model/filterOptions'
import { groupByDate, smartDefaultDay } from '../model/grouping'
import { getProgrammingKind } from '../model/kind'
import { computeScheduleStats } from '../model/stats'
import { getTemporalStatus } from '../model/temporal'
import {
  DEFAULT_SCHEDULE_TIME_FILTER,
  pickScheduleTimeFilter,
  type ScheduleTimeFilter,
  type Session
} from '../model/types'
import { buildScheduleTimeFilterOptions } from '../lib/scheduleFilterOptions'
import { useProgrammingKinds } from './useProgrammingKinds'
import { useScheduleFilters } from './useScheduleFilters'

/**
 * `LoadWrapper` é JSX sem tipos — o TS infere todas as props do destructuring
 * como obrigatórias. Derivar a assinatura por `typeof import` mantém o tipo
 * alinhado ao componente sem trazê-lo para o runtime do hook. Tipar
 * `shared/ui/LoadWrapper` é trabalho do design system, não deste módulo.
 */
type LoadWrapperProps = ComponentProps<
  (typeof import('@/shared/ui'))['LoadWrapper']
>

type ScheduleRecordsContext = {
  records?: Session[]
  refetch?: () => void
  loadWrapperConfig?: LoadWrapperProps
  config: {
    humanName: { singular: string; plural: string }
  }
}

/**
 * Estado de tela do módulo e derivações sobre a coleção de sessões.
 *
 * Toda a lógica de filtro, agrupamento e destaque vive em `model/` — aqui só
 * há estado local e memoização. Dia, filtro e busca são efêmeros: não
 * sobrevivem à navegação, não são compartilhados entre rotas e não vêm do
 * servidor, por isso `useState` e não Zustand, `nuqs` ou TanStack Query.
 */
export const useScheduleView = () => {
  const { t } = useTranslation()
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [rawTimeFilter, setRawTimeFilter] = useState<ScheduleTimeFilter>(
    DEFAULT_SCHEDULE_TIME_FILTER
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [detailSession, setDetailSession] = useState<Session | null>(null)

  const advancedFilters = useScheduleFilters()
  const userChangedDayRef = useRef(false)
  const catalogKinds = useProgrammingKinds()

  const {
    records,
    refetch,
    loadWrapperConfig,
    config: { humanName }
  } = useResourceRecordsContext() as ScheduleRecordsContext

  const days = useMemo(() => groupByDate(records ?? []), [records])

  // Stats computados sobre TODAS as sessões (não filtradas).
  const stats = useMemo(() => computeScheduleStats(records ?? []), [records])

  // Opções de filtro derivadas do evento INTEIRO, não do dia selecionado.
  // Derivar do dia era o defeito reportado: local e palestrante existentes na
  // base sumiam do filtro por não estarem naquele recorte, e o participante não
  // achava a atividade.
  //
  // O rótulo do tipo tenta, nesta ordem: a tradução local, o nome que a api
  // manda no catálogo (cobre tipo novo no enum sem deploy daqui) e, por último,
  // o próprio slug.
  const resolveKindLabel = useCallback(
    (slug: string) => {
      const entry = getProgrammingKind(slug)

      if (entry.slug !== 'unknown') return t(entry.nameKey)

      return catalogKinds.find(kind => kind.slug === slug)?.name ?? slug
    },
    [catalogKinds, t]
  )

  const filterOptions = useMemo(
    () => deriveFilterOptions(records ?? [], { resolveKindLabel }),
    [records, resolveKindLabel]
  )

  // Na abertura da tela, prioriza hoje. Depois que o participante troca de dia,
  // mantém a escolha até sair da rota (o ref zera no próximo mount).
  useEffect(() => {
    setSelectedDay(previous => {
      if (!userChangedDayRef.current) {
        return smartDefaultDay(days)
      }

      return previous && days[previous] ? previous : smartDefaultDay(days)
    })
  }, [days])

  const handleSetSelectedDay = (day: string | null) => {
    userChangedDayRef.current = true
    setSelectedDay(day)
    advancedFilters.reset()
  }

  const daySessions = useMemo(
    () => (selectedDay ? (days[selectedDay] ?? []) : []),
    [days, selectedDay]
  )

  // Pipeline: busca → filtros avançados → filtro de momento → partição.
  const searched = useMemo(
    () => searchSessions(daySessions, searchQuery),
    [daySessions, searchQuery]
  )

  const advancedFiltered = useMemo(
    () => applyAdvancedFilters(searched, advancedFilters.filters),
    [searched, advancedFilters.filters]
  )

  const timeFilterOptions = useMemo(
    () => buildScheduleTimeFilterOptions(t),
    [t]
  )

  const matchesTimeFilter = (id: ScheduleTimeFilter, sessions: Session[]) => {
    if (id === 'meu-checkin') {
      return sessions.some(session => session.finished)
    }

    const target =
      id === 'agora' ? 'live' : id === 'proximas' ? 'future' : 'past'

    return sessions.some(
      session =>
        getTemporalStatus(session.start_at, session.end_at) === target
    )
  }

  const visibleTimeFilters = useMemo(() => {
    if (advancedFiltered.length === 0) return []

    return timeFilterOptions.filter(option =>
      matchesTimeFilter(option.id, advancedFiltered)
    )
  }, [advancedFiltered, timeFilterOptions])

  const timeFilter = useMemo(
    () =>
      pickScheduleTimeFilter(
        rawTimeFilter,
        visibleTimeFilters.map(option => option.id)
      ),
    [rawTimeFilter, visibleTimeFilters]
  )

  const filtered = useMemo(
    () => filterByMoment(advancedFiltered, timeFilter),
    [advancedFiltered, timeFilter]
  )

  const { live, future, past } = useMemo(
    () => partitionByTemporalStatus(filtered),
    [filtered]
  )

  const hasActiveFilters =
    rawTimeFilter !== DEFAULT_SCHEDULE_TIME_FILTER ||
    advancedFilters.hasActiveFilters ||
    Boolean(searchQuery.trim())

  const clearFilters = () => {
    setRawTimeFilter(DEFAULT_SCHEDULE_TIME_FILTER)
    setSearchQuery('')
    advancedFilters.reset()
  }

  return {
    days,
    selectedDay,
    setSelectedDay: handleSetSelectedDay,
    timeFilter,
    setTimeFilter: setRawTimeFilter,
    visibleTimeFilters,
    searchQuery,
    setSearchQuery,
    live,
    future,
    past,
    filteredSessions: filtered,
    isEmpty: filtered.length === 0,
    hasSearch: Boolean(searchQuery.trim()),
    hasActiveFilters,
    clearFilters,
    humanName,
    loadWrapperConfig,
    refetch,
    // Advanced filters
    advancedFilters,
    filterOptions,
    // Stats
    stats,
    // Detail sheet
    detailSession,
    setDetailSession
  }
}
